/**
 * What a reader can DO about a nagging reminder, without leaving the bell.
 *
 * The customer complaint this answers: a stale-contact alert's only affordance was to
 * navigate to the contact, so the reader was dropped on a record page to work out what the
 * alert wanted. The strip puts the alert's own options where the alert is — log the
 * interaction that CLEARS it, silence it for a while, schedule a personal follow-up, stop
 * being told about it, or go to the record after all.
 *
 * Framework-level on purpose: it is mounted for any `reminder.*` row carrying a
 * `reminder_state_id`, which is ~30 rules across 11 modules. `reminderActions.ts` decides
 * per row which of the six buttons apply, so a rule whose entity takes no interactions
 * simply renders fewer.
 *
 * **Nothing here opens a floating layer, and that is load-bearing.** This renders INSIDE
 * the bell's Radix `Popover`, and every portalled surface — a `DropdownMenu` of presets, a
 * nested popover, a modal — portals to `body`, so a pointerdown inside it is an OUTSIDE
 * pointerdown to the popover, which unmounts this component mid-interaction. So the snooze
 * presets, the follow-up composer and the dismiss confirmation are all INLINE modes of this
 * strip, and the one genuinely modal action ("Log an activity") is not rendered here at all
 * — it is raised to the bell, which stays mounted when the popover closes.
 * (`LocalizedDateTimeInput` is two native inputs and portals nothing, so it is safe.)
 *
 * **It never trusts the notification for STATE.** An inbox row is an emit-time snapshot, so
 * `activeReminder` — looked up in `GET /api/v1/reminders/active` by the bell — is the live
 * answer, and its absence means the nag has been resolved, dismissed or expired since it
 * was sent. Snooze and dismiss are withheld in that case rather than offered and 404'd.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlarmClock,
  ArrowRight,
  BellOff,
  CalendarClock,
  Clock,
  NotebookPen,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { LocalizedDateTimeDisplay } from '../components/LocalizedDateTimeDisplay';
import { LocalizedDateTimeInput } from '../components/LocalizedDateTimeInput';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { getEntityUrl } from '../utils/entityUrls';
import { LegacySpace, tryToLegacy } from '../utils/entityVocabulary';
import { SNOOZE_PRESETS, presetInstant } from './reminderPresets';
import { CADENCE_FOCUS_PARAM, type ReminderActionTarget } from './reminderActions';
import { useCreateEntityReminder } from './useEntityReminders';
import {
  useDismissReminder,
  useSnoozeReminder,
  type ActiveReminderResponse,
} from './useNagReminders';

/** Which inline sub-form the strip is showing, if any. */
type Mode = 'actions' | 'snooze' | 'snoozeDate' | 'followUp' | 'confirmStop';

export interface ReminderActionStripProps {
  target: ReminderActionTarget;
  /**
   * The live `reminder_states` row, or undefined when this nag is no longer active (or
   * has not been looked up). Undefined withholds the nag-scoped actions; it never
   * withholds the record-scoped ones, which are still perfectly valid.
   */
  activeReminder: ActiveReminderResponse | undefined;
  /** Navigate and close the bell — the bell owns both halves. */
  onNavigate: (href: string) => void;
  /**
   * Ask the BELL to open the interaction dialog. Raised rather than rendered here because
   * a modal portals outside the popover and would unmount this component with it.
   */
  onLogActivity: (target: ReminderActionTarget) => void;
  /** `id` of the region this strip is disclosed by, for `aria-controls`. */
  id: string;
}

/**
 * The record's page with the cadence card asked for by name.
 *
 * `&` where the registry template already carries a query string — a contact's does
 * not today, but a template gaining a `?tab=` later would otherwise produce a URL with
 * two `?` in it and lose the focus silently.
 */
function withCadenceFocus(href: string): string {
  return `${href}${href.includes('?') ? '&' : '?'}${CADENCE_FOCUS_PARAM}`;
}

/** The record's own page, or null when nothing addressable resolves. */
function recordHref(target: ReminderActionTarget): string | null {
  if (!target.entityType || !target.entityId) return null;
  return getEntityUrl({
    entityType: target.entityType,
    entityId: target.entityId,
    // Persisted rows, so this runs over historical entity types too: a
    // convention-guessed URL for one of those is worse than no link at all.
  });
}

export function ReminderActionStrip({
  target,
  activeReminder,
  onNavigate,
  onLogActivity,
  id,
}: ReminderActionStripProps) {
  const { t } = useTranslation(['common', 'contacts']);
  const { timezone } = useUserPreferences();
  const [mode, setMode] = useState<Mode>('actions');
  const [customInstant, setCustomInstant] = useState<string | null>(null);
  const [followUpNote, setFollowUpNote] = useState('');

  const snooze = useSnoozeReminder();
  const dismiss = useDismissReminder();
  const createFollowUp = useCreateEntityReminder();

  const href = recordHref(target);
  const stateId = target.reminderStateId;
  // The nag-scoped actions need BOTH an id to act on and a live row to act on it — an id
  // whose row this endpoint no longer returns has already been resolved or dismissed.
  const canActOnNag = stateId !== null && activeReminder !== undefined;
  const followEntityType = target.entityType
    ? tryToLegacy(target.entityType, LegacySpace.FollowEntityType)
    : null;
  const busy = snooze.isPending || dismiss.isPending || createFollowUp.isPending;
  // A hand-picked instant in the past is a rejected create, and the API's refusal is a
  // generic VALIDATION_ERROR — which translates to "Please check the form for errors"
  // and so says nothing about what to do. Refuse it here with copy that does.
  const customIsPast =
    customInstant !== null && new Date(customInstant).getTime() <= Date.now();

  const runSnooze = (instant: string) => {
    if (!stateId) return;
    snooze.mutate(
      { reminderId: stateId, data: { snooze_until: instant } },
      {
        onSuccess: () => {
          toast.success(t('common:notifications.reminderActions.toastSnoozed'));
          setMode('actions');
        },
      },
    );
  };

  const runDismiss = () => {
    if (!stateId) return;
    dismiss.mutate(
      { reminderId: stateId },
      {
        onSuccess: () => {
          toast.success(t('common:notifications.reminderActions.toastStopped'));
          setMode('actions');
        },
      },
    );
  };

  const runFollowUp = () => {
    if (!followEntityType || !target.entityId || !customInstant) return;
    createFollowUp.mutate(
      {
        data: {
          entity_type: followEntityType,
          entity_id: target.entityId,
          remind_at: customInstant,
          note: followUpNote.trim() || null,
          // Null, not [], so the API applies its own "the creator alone" default.
          recipient_user_ids: null,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('common:reminders.toastCreated'));
          setCustomInstant(null);
          setFollowUpNote('');
          setMode('actions');
        },
      },
    );
  };

  // Named rather than spelled inline in `onClick`: `i18next/no-literal-string` scans
  // JSX attributes and reads a mode name as user-visible copy.
  const showSnoozePresets = () => setMode('snooze');
  const showSnoozeDate = () => setMode('snoozeDate');
  const showFollowUp = () => setMode('followUp');
  const showConfirmStop = () => setMode('confirmStop');

  const cancelButton = (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 text-xs"
      onClick={() => setMode('actions')}
      disabled={busy}
    >
      {t('common:app.cancel')}
    </Button>
  );

  return (
    <div id={id} className="space-y-2 border-b bg-muted/40 px-3 py-2">
      <ReminderDetailLine target={target} activeReminder={activeReminder} />

      {mode === 'actions' && (
        <div className="flex flex-wrap gap-2">
          {target.interactionSubject && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => onLogActivity(target)}
            >
              <NotebookPen className="h-3.5 w-3.5" />
              {t('common:notifications.reminderActions.logActivity')}
            </Button>
          )}
          {canActOnNag && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={showSnoozePresets}
            >
              <Clock className="h-3.5 w-3.5" />
              {t('common:notifications.reminderActions.snooze')}
            </Button>
          )}
          {followEntityType && target.entityId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={showFollowUp}
            >
              <AlarmClock className="h-3.5 w-3.5" />
              {t('common:reminders.remindMe')}
            </Button>
          )}
          {target.canChangeCadence && href && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => onNavigate(withCadenceFocus(href))}
            >
              <CalendarClock className="h-3.5 w-3.5" />
              {t('contacts:cadence.title')}
            </Button>
          )}
          {canActOnNag && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={showConfirmStop}
            >
              <BellOff className="h-3.5 w-3.5" />
              {t('common:notifications.reminderActions.stopReminding')}
            </Button>
          )}
          {href && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => onNavigate(href)}
            >
              <ArrowRight className="h-3.5 w-3.5" />
              {t('common:notifications.reminderActions.open')}
            </Button>
          )}
        </div>
      )}

      {mode === 'snooze' && (
        <div className="flex flex-wrap gap-2">
          {SNOOZE_PRESETS.map((preset) => {
            const instant = presetInstant(preset.id, new Date(), timezone);
            // A preset already in the past is a rejected create, not an option.
            if (!instant) return null;
            return (
              <Button
                key={preset.id}
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => runSnooze(instant)}
                disabled={busy}
              >
                {t(preset.labelKey)}
              </Button>
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={showSnoozeDate}
            disabled={busy}
          >
            {t('common:reminders.customTime')}
          </Button>
          {cancelButton}
        </div>
      )}

      {mode === 'snoozeDate' && (
        <div className="space-y-2">
          <LocalizedDateTimeInput
            value={customInstant}
            onChange={setCustomInstant}
            label={t('common:reminders.customTimeLabel')}
            size="sm"
            fullWidth
            disabled={busy}
          />
          {customIsPast && (
            <p className="text-xs text-destructive">{t('common:reminders.pastTime')}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => customInstant && runSnooze(customInstant)}
              disabled={busy || !customInstant || customIsPast}
            >
              {t('common:notifications.reminderActions.snooze')}
            </Button>
            {cancelButton}
          </div>
        </div>
      )}

      {mode === 'followUp' && (
        <div className="space-y-2">
          <LocalizedDateTimeInput
            value={customInstant}
            onChange={setCustomInstant}
            label={t('common:reminders.whenLabel')}
            size="sm"
            fullWidth
            disabled={busy}
          />
          <Textarea
            value={followUpNote}
            onChange={(event) => setFollowUpNote(event.target.value)}
            placeholder={t('common:reminders.notePlaceholder')}
            aria-label={t('common:reminders.noteLabel')}
            rows={2}
            className="text-xs"
            disabled={busy}
          />
          {customIsPast && (
            <p className="text-xs text-destructive">{t('common:reminders.pastTime')}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={runFollowUp}
              disabled={busy || !customInstant || customIsPast}
            >
              {t('common:reminders.submit')}
            </Button>
            {cancelButton}
          </div>
        </div>
      )}

      {mode === 'confirmStop' && (
        <div className="space-y-2">
          {/* The consequence, spelled from the RULE rather than assumed. A rule that does
              not re-arm keeps its dedup key on a dismissed row forever, so for those the
              reader is agreeing to something with no undo anywhere in the product. */}
          <p className="text-xs text-muted-foreground">
            {target.ruleRearms
              ? t('common:notifications.reminderActions.stopRemindingUntilNext')
              : t('common:notifications.reminderActions.stopRemindingPermanent')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="destructive"
              size="sm"
              className="h-8 text-xs"
              onClick={runDismiss}
              disabled={busy}
            >
              {t('common:notifications.reminderActions.stopReminding')}
            </Button>
            {cancelButton}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The one line of context the alert itself can supply.
 *
 * Three facts, each rendered only where it is known: how long the relationship has been
 * quiet and what cadence it was measured against (supplied by the gone-quiet rule's own
 * payload; most rules carry neither), and — the live one — when a snoozed nag comes back.
 */
function ReminderDetailLine({
  target,
  activeReminder,
}: {
  target: ReminderActionTarget;
  activeReminder: ActiveReminderResponse | undefined;
}) {
  const { t } = useTranslation('common');
  const parts: string[] = [];
  if (target.daysQuiet !== null) {
    parts.push(
      t('notifications.reminderActions.lastInteraction', { count: target.daysQuiet }),
    );
  }
  if (target.cadenceDays !== null && target.cadenceDays > 0) {
    parts.push(t('notifications.reminderActions.cadence', { count: target.cadenceDays }));
  }

  const snoozedUntil =
    activeReminder?.status === 'snoozed' ? activeReminder.snooze_until : null;

  if (parts.length === 0 && !snoozedUntil && activeReminder !== undefined) return null;

  return (
    <p className="text-[11px] text-muted-foreground">
      {/* No `dir` and no `BidiText`: both halves are our own `t()` output, whose
          language IS the UI locale, so there is nothing to detect and the
          first-strong heuristic could only get it wrong (STYLE_GUIDE § Bidi). */}
      {parts.length > 0 && parts.join(' · ')}
      {snoozedUntil && (
        <span className="block">
          {t('notifications.reminderActions.snoozedUntil')}{' '}
          <LocalizedDateTimeDisplay date={snoozedUntil} />
        </span>
      )}
      {activeReminder === undefined && (
        <span className="block">{t('notifications.reminderActions.noLongerActive')}</span>
      )}
    </p>
  );
}
